/**
 * The product-list layout shared by department, Shopify-collection and search pages: breadcrumbs,
 * a left sidebar (department tree + collapsible filters), a toolbar (count, per page, sort,
 * grid/list) and numbered pagination. Page, per-page, sort, view and every filter live in the URL
 * query, so refresh, Back and a shared link all land on the same view.
 */
import { useMemo, useState, type ReactNode } from "react";
import type { Facet, NavNode, ShopCard } from "./types";
import { Go, qsSet, type GoFn, type Route } from "./route";
import { deptRoute, pathTo } from "./nav";
import { rands } from "./shared";
import { Pic } from "./img";
import { IconChevronRight, IconFilter, IconGrid, IconList } from "./icons";
import { Drawer } from "./ui";
import { RatingStars } from "./ProductReviews";

export function ProductCard({ p, i, go, trade }: { p: ShopCard; i: number; go: GoFn; trade: boolean }) {
  const saving = p.wasCents && p.priceCents && p.wasCents > p.priceCents ? p.wasCents - p.priceCents : 0;
  return (
    <article className="card">
      <Go to={{ name: "product", handle: p.handle }} go={go} className="card-link">
        <div className="card-media">
          {p.image ? <Pic src={p.image} alt={p.imageAlt ?? p.title} sizes="(min-width: 1100px) 22vw, 46vw" eager={i < 2} /> : <div className="skel fill" />}
          {saving > 0 && <span className="save-flag money">Save {rands(saving)}</span>}
          {p.deal && <span className={`deal-flag ${p.deal.kind === "clearance" ? "clr" : "day"}`}>{p.deal.kind === "clearance" ? "Clearance" : "Deal"}</span>}
        </div>
        {p.vendor && <div className="card-brand">{p.vendor}</div>}
        <h3>{p.title}</h3>
        {p.rating && p.rating.count > 0 && (
          <div className="card-rating"><RatingStars average={p.rating.average} count={p.rating.count} size="sm" inLink /><span aria-hidden="true">({p.rating.count})</span></div>
        )}
        <div className="card-price money">
          <b className={saving > 0 ? "sale" : undefined}>{rands(p.priceCents)}</b>
          {saving > 0 && <s>{rands(p.wasCents)}</s>}
          {/* Trade on clearance pays the lower of the two (server-side); the tag names which one this is. */}
          {trade && p.priceCents !== null && <span className="trade-tag">{p.deal?.kind === "clearance" && saving > 0 ? "Clearance price" : "Wholesale price"}</span>}
        </div>
        <span className={`stock${p.inStock ? "" : " out"}`}><i aria-hidden="true" />{p.inStock ? "In stock" : "Sold out"}</span>
      </Go>
    </article>
  );
}

/** The department tree for the sidebar: the current branch is expanded, counts in brackets. */
export function DeptTree({ tree, currentId, go }: { tree: NavNode[]; currentId: string | null; go: GoFn }) {
  const branch = currentId ? pathTo(tree, currentId) : [];
  const ids = new Set(branch.map((n) => n.id));
  const render = (nodes: NavNode[]): ReactNode => (
    <ul>
      {nodes.map((n) => (
        <li key={n.id}>
          <Go to={deptRoute(n)} go={go} className={n.id === currentId ? "on" : undefined} ariaCurrent={n.id === currentId ? "page" : undefined}>
            {n.name} <span className="n">({n.productCount})</span>
          </Go>
          {ids.has(n.id) && n.children.length > 0 && render(n.children)}
        </li>
      ))}
    </ul>
  );
  if (tree.length === 0) return null;
  return <nav className="side-block tree" aria-label="Departments"><h2>Departments</h2>{render(tree)}</nav>;
}

/* ── URL-held view state ───────────────────────────────────────────────────────────────────── */
type Sort = "featured" | "price-asc" | "price-desc" | "name";
const SORTS: Array<[Sort, string]> = [["featured", "Featured"], ["price-asc", "Price: low to high"], ["price-desc", "Price: high to low"], ["name", "A–Z"]];
const PER = [24, 48, 96];

function readView(qs: string) {
  const p = new URLSearchParams(qs);
  const per = Number(p.get("per"));
  const sort = p.get("sort") as Sort | null;
  const chosen: Record<string, Set<string>> = {};
  p.forEach((v, k) => {
    if (["page", "per", "sort", "view", "min", "max", "stock", "q", "in", "code", "mock"].includes(k)) return;
    (chosen[k] ??= new Set()).add(v);
  });
  const min = Number(p.get("min"));
  const max = Number(p.get("max"));
  return {
    page: Math.max(1, Math.floor(Number(p.get("page")) || 1)),
    per: PER.includes(per) ? per : 24,
    sort: SORTS.some(([s]) => s === sort) ? (sort as Sort) : "featured",
    list: p.get("view") === "list",
    stock: p.get("stock") === "1",
    min: p.get("min") && Number.isFinite(min) ? min : null,
    max: p.get("max") && Number.isFinite(max) ? max : null,
    chosen,
  };
}

/** Brand and type facets derived from the cards themselves, for pages whose API gives none (search). */
export function facetsFromCards(products: ShopCard[]): Facet[] {
  const tally = (pick: (p: ShopCard) => string | null) => {
    const m = new Map<string, number>();
    products.forEach((p) => { const v = pick(p); if (v) m.set(v, (m.get(v) ?? 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, label: value, count }));
  };
  const out: Facet[] = [];
  const brands = tally((p) => p.vendor);
  if (brands.length > 1) out.push({ key: "vendor", label: "Brand", options: brands });
  const types = tally((p) => p.type);
  if (types.length > 1) out.push({ key: "type", label: "Type", options: types });
  return out;
}

function FilterGroups({ facets, priceRange, view, change }: {
  facets: Facet[]; priceRange: { minCents: number; maxCents: number } | null; view: ReturnType<typeof readView>;
  change: (c: Record<string, string | string[] | null>) => void;
}) {
  const [lo, setLo] = useState(view.min?.toString() ?? "");
  const [hi, setHi] = useState(view.max?.toString() ?? "");
  const shown = facets.filter((f) => f.key !== "stock" && f.options.length > 0);
  return (
    <div className="filters-in">
      <details open className="fgroup">
        <summary>Availability</summary>
        <label className="fopt"><input type="checkbox" checked={view.stock} onChange={() => change({ stock: view.stock ? null : "1", page: null })} /><span>In stock only</span></label>
      </details>
      {priceRange && priceRange.maxCents > priceRange.minCents && (
        <details open className="fgroup">
          <summary>Price</summary>
          <form className="fprice" onSubmit={(e) => { e.preventDefault(); change({ min: lo.trim() || null, max: hi.trim() || null, page: null }); }}>
            <label><span className="sr-only">Minimum price in rand</span><input inputMode="numeric" placeholder={`R${Math.floor(priceRange.minCents / 100)}`} value={lo} onChange={(e) => setLo(e.target.value.replace(/\D/g, ""))} /></label>
            <span aria-hidden="true">–</span>
            <label><span className="sr-only">Maximum price in rand</span><input inputMode="numeric" placeholder={`R${Math.ceil(priceRange.maxCents / 100)}`} value={hi} onChange={(e) => setHi(e.target.value.replace(/\D/g, ""))} /></label>
            <button type="submit" className="btn btn-quiet btn-sm">Go</button>
          </form>
        </details>
      )}
      {shown.map((f, gi) => (
        <details key={f.key} open={gi < 2} className="fgroup">
          <summary>{f.label}</summary>
          {f.options.map((o) => {
            const on = view.chosen[f.key]?.has(o.value) ?? false;
            const next = new Set(view.chosen[f.key] ?? []);
            if (on) next.delete(o.value); else next.add(o.value);
            return (
              <label key={o.value} className="fopt">
                <input type="checkbox" checked={on} onChange={() => change({ [f.key]: next.size ? [...next] : null, page: null })} />
                <span>{o.label}</span><span className="n">{o.count}</span>
              </label>
            );
          })}
        </details>
      ))}
    </div>
  );
}

function pageWindow(page: number, pages: number): Array<number | "gap"> {
  const out: Array<number | "gap"> = [];
  for (let n = 1; n <= pages; n++) {
    if (n === 1 || n === pages || Math.abs(n - page) <= 1) out.push(n);
    else if (out[out.length - 1] !== "gap") out.push("gap");
  }
  return out;
}

export interface Crumbish { label: string; to?: Route }

export function Listing({ title, crumbs, intro, facts, guides, banner, products, facets, priceRange, qs, mk, go, trade, tree, currentId, sideExtra, aboveGrid, emptyHint }: {
  title: string; crumbs: Crumbish[]; intro?: ReactNode; facts?: string; guides?: Array<{ slug: string; title: string }>; banner?: string | null; products: ShopCard[]; facets: Facet[];
  priceRange: { minCents: number; maxCents: number } | null; qs: string; mk: (qs: string) => Route; go: GoFn; trade: boolean;
  tree: NavNode[]; currentId: string | null; sideExtra?: ReactNode; aboveGrid?: ReactNode; emptyHint?: ReactNode;
}) {
  const view = readView(qs);
  const [sheet, setSheet] = useState(false);
  const change = (c: Record<string, string | string[] | null>) => go(mk(qsSet(qs, c)), { keepScroll: true });

  const filtered = useMemo(() => {
    const items = products.filter((p) => {
      if (view.stock && !p.inStock) return false;
      if (view.min !== null && (p.priceCents ?? 0) < view.min * 100) return false;
      if (view.max !== null && (p.priceCents ?? Infinity) > view.max * 100) return false;
      for (const [key, values] of Object.entries(view.chosen)) {
        if (values.size === 0) continue;
        if (key === "vendor" && !(p.vendor && values.has(p.vendor))) return false;
        else if (key === "type" && !(p.type && values.has(p.type))) return false;
        else if (key.startsWith("opt:")) {
          const hay = `${p.title} ${p.tags.join(" ")}`.toLowerCase();
          if (![...values].some((v) => hay.includes(v.toLowerCase()))) return false;
        }
      }
      return true;
    });
    const by = [...items];
    if (view.sort === "price-asc") by.sort((a, b) => (a.priceCents ?? Infinity) - (b.priceCents ?? Infinity));
    else if (view.sort === "price-desc") by.sort((a, b) => (b.priceCents ?? -1) - (a.priceCents ?? -1));
    else if (view.sort === "name") by.sort((a, b) => a.title.localeCompare(b.title));
    else by.sort((a, b) => Number(b.inStock) - Number(a.inStock));
    return by;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, qs]);

  const pages = Math.max(1, Math.ceil(filtered.length / view.per));
  const page = Math.min(view.page, pages);
  const from = filtered.length === 0 ? 0 : (page - 1) * view.per + 1;
  const to = Math.min(filtered.length, page * view.per);
  const slice = filtered.slice((page - 1) * view.per, page * view.per);
  const activeCount = Object.values(view.chosen).reduce((n, s) => n + s.size, 0) + (view.stock ? 1 : 0) + (view.min !== null ? 1 : 0) + (view.max !== null ? 1 : 0);
  const clearKeys: Record<string, null> = { stock: null, min: null, max: null, page: null };
  for (const k of Object.keys(view.chosen)) clearKeys[k] = null;

  const filters = <FilterGroups key={`${view.min}-${view.max}`} facets={facets} priceRange={priceRange} view={view} change={change} />;

  return (
    <div className="wrap list-page">
      <nav className="crumbs" aria-label="Breadcrumb">
        <ol>
          {crumbs.map((c, i) => (
            <li key={`${c.label}-${i}`}>
              {c.to ? <Go to={c.to} go={go}>{c.label}</Go> : <span aria-current="page">{c.label}</span>}
              {i < crumbs.length - 1 && <IconChevronRight size={12} />}
            </li>
          ))}
        </ol>
      </nav>
      <div className="list-layout">
        <aside className="side" aria-label="Browse and filter">
          <DeptTree tree={tree} currentId={currentId} go={go} />
          {sideExtra}
          <div className="side-block"><h2>Filters</h2>{filters}</div>
        </aside>
        <div className="list-main">
          {banner && <div className="list-banner"><Pic src={banner} alt={title} sizes="(min-width: 1100px) 70vw, 100vw" width={1200} height={300} eager /></div>}
          <h1>{title}</h1>
          {intro && <p className="list-intro">{intro}</p>}
          {facts && <p className="list-intro list-facts">{facts}</p>}
          {guides && guides.length > 0 && (
            <p className="list-guides">Buying guides: {guides.map((g, i) => <span key={g.slug}>{i > 0 && " · "}<Go to={{ name: "guide", slug: g.slug }} go={go}>{g.title}</Go></span>)}</p>
          )}
          {aboveGrid}
          <div className="toolbar">
            <button type="button" className="btn btn-quiet tb-filters" onClick={() => setSheet(true)}><IconFilter size={18} /> Filters{activeCount > 0 ? ` (${activeCount})` : ""}</button>
            <p className="tb-count" aria-live="polite">{filtered.length === 0 ? "No products" : <>Showing <b className="money">{from}–{to}</b> of <b className="money">{filtered.length}</b> products</>}</p>
            <label className="tb-sel tb-per"><span>Per page</span>
              <select value={view.per} onChange={(e) => change({ per: e.target.value === "24" ? null : e.target.value, page: null })}>{PER.map((n) => <option key={n} value={n}>{n}</option>)}</select>
            </label>
            <label className="tb-sel"><span>Sort</span>
              <select value={view.sort} onChange={(e) => change({ sort: e.target.value === "featured" ? null : e.target.value, page: null })}>{SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </label>
            <div className="tb-view" role="group" aria-label="Layout">
              <button type="button" aria-pressed={!view.list} aria-label="Grid view" onClick={() => change({ view: null })}><IconGrid size={18} /></button>
              <button type="button" aria-pressed={view.list} aria-label="List view" onClick={() => change({ view: "list" })}><IconList size={18} /></button>
            </div>
          </div>
          {activeCount > 0 && (
            <div className="active-filters">
              <button type="button" className="chip" onClick={() => change(clearKeys)}>Clear {activeCount} filter{activeCount === 1 ? "" : "s"} ×</button>
            </div>
          )}
          {slice.length === 0 ? (
            <div className="empty"><h2>Nothing matches</h2><p>{emptyHint ?? "Clear a filter and we will widen the search."}</p>{activeCount > 0 && <button type="button" className="btn btn-quiet" onClick={() => change(clearKeys)}>Clear filters</button>}</div>
          ) : (
            <div className={`grid${view.list ? " grid-list" : ""}`}>{slice.map((p, i) => <ProductCard key={p.handle} p={p} i={i} go={go} trade={trade} />)}</div>
          )}
          {pages > 1 && (
            <nav className="pager" aria-label="Pagination">
              {page > 1 && <Go to={mk(qsSet(qs, { page: page - 1 === 1 ? null : String(page - 1) }))} go={go}>Previous</Go>}
              {pageWindow(page, pages).map((n, i) => n === "gap"
                ? <span key={`g${i}`} className="gap" aria-hidden="true">…</span>
                : <Go key={n} to={mk(qsSet(qs, { page: n === 1 ? null : String(n) }))} go={go} className={n === page ? "on" : undefined} ariaCurrent={n === page ? "page" : undefined}>{n}</Go>)}
              {page < pages && <Go to={mk(qsSet(qs, { page: String(page + 1) }))} go={go}>Next</Go>}
            </nav>
          )}
        </div>
      </div>
      <Drawer
        open={sheet}
        onClose={() => setSheet(false)}
        side="bottom"
        title="Filters"
        footer={<button type="button" className="btn btn-action btn-block" onClick={() => setSheet(false)}>Show {filtered.length} result{filtered.length === 1 ? "" : "s"}</button>}
      >
        {filters}
      </Drawer>
    </div>
  );
}
